import type { Game } from 'chessboard/model/game/types';
import type { API as AppAPI } from 'chessboard/api/config/types';
import type { Board } from 'chessboard/model/board/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';

export namespace Modes {
  export interface AdditionalMethods {
    [key: string]: (...args: any) => any;
  }

  export namespace CreateMode {
    export type Base<T extends {} = {}> = ({ api, options }: BaseOptions<T>) => API.Base;

    export type Builder<T extends Options.Initial.Custom = {}> = (
      api: AppAPI.Model,
    ) => API.Extended<T>;

    export type Factory<T extends Options.Initial.Custom = {}> = (
      options?: Partial<Options.Exposed.Model<T>>,
    ) => Builder<T>;

    export interface Module<T extends Options.Initial.Custom = {}> {
      create: Factory<T>;
    }

    export interface BaseOptions<T extends {} = {}> {
      api: AppAPI.ExtendedAPI;
      options: Options.Exposed.Model<T>;
    }

    export interface SetMode {
      modeType: Modes.Types;
      options?: Partial<Modes.Options.Exposed.Model>;
    }
  }

  export namespace Options {
    export namespace Initial {
      export interface Global {
        canAddMovesToMainLine: Option;
        canInteractWithPieces: Option;
        canModifyExistingMovesOnMainLine: Option;
      }

      export type Model<T extends Custom = {}> = T & Global;

      export interface Option {
        default: boolean;
        editable: boolean;
      }

      export type Custom = { [key: string]: Option };
    }

    export namespace Exposed {
      export type Model<T extends {} = {}> = {
        [key in keyof Initial.Model<T>]: boolean;
      };
    }
  }

  export namespace API {
    export interface Base {
      additionalMethods?: AdditionalMethods;
      boardOptions?: Partial<Board.Options>;
      destroy?: () => void;
      handleIllegalMove?: (moveAttempt?: Game.Move.Attempt) => void;
      isAllowedToMove: (color?: Game.ColorsAsNumbers) => boolean;
      name: Types;
      overridableMethods?: string[];
      plugins?: Plugins.Model[];
      usePlayingAs?: boolean;
    }
    export interface Bound<T extends Options.Initial.Custom = {}> extends Base {
      getOptions: () => { [key in keyof Options.Initial.Model<T>]: boolean };
      setOption: (key: keyof Options.Initial.Model<T>, value: boolean) => void;
    }

    export interface Extended<T extends Options.Initial.Custom = {}> extends API.Bound<T> {
      extendMode: (extensions: Extension) => () => void;
    }

    export type Extension = Record<string, Function>;
  }

  export enum Types {
    Analysis = 'analysis',
    CaptureTheStar = 'capture-the-star',
    Explorer = 'exploring',
    GuessTheMove = 'guess-the-move',
    PassiveObserving = 'passive-observing',
    Observing = 'observing',
    Playing = 'playing',
    Setup = 'setup',
  }
}
